import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { BACKEND_URI } from "../config/config-vars";
import {
  FusionConnectDailyStat,
  FusionConnectDailyStatPage,
  FusionConnectGameMeterRow,
  FusionConnectRead,
  FusionConnectReadPage,
  FusionConnectReadRequest,
} from "../models/fusion-connect.model";
import { Page } from "../models/page.model";
import { LoginService } from "./login.service";

const BASE_URL = `${BACKEND_URI}/api/fusion-connect`;
const READS_URLS = [`${BASE_URL}/reads`];
const DAILY_STATS_URLS = [`${BASE_URL}/stats/daily`];
const DAILY_STATS_REBUILD_URL = `${BASE_URL}/stats/daily/rebuild`;
const DAILY_STATS_MUTATION_BASE_URL = `${BASE_URL}/stats/daily`;
const CREATE_REGULAR_BOARD_STAT_URL = `${BASE_URL}/reads/create-board-stat`;

@Injectable({ providedIn: "root" })
export class FusionConnectService {
  constructor(private http: HttpClient, private loginService: LoginService) {}

  async ingestRead(payload: FusionConnectReadRequest): Promise<FusionConnectRead | null> {
    if (!this.loginService.isloggedIn) {
      console.warn("[FusionConnect] ingestRead skipped: user not logged in");
      return null;
    }

    return await firstValueFrom(this.http.post<FusionConnectRead>(READS_URLS[0], payload));
  }

  async fetchReads(params: {
    page?: number;
    pageSize?: number;
    boardId?: string;
  } = {}): Promise<FusionConnectReadPage> {
    if (!this.loginService.isloggedIn) {
      return this.emptyPage<FusionConnectRead>();
    }

    const paramVariants = this.buildParamsVariants(params, ["boardId"]);
    const res = await this.getWithFallback<any>(READS_URLS, paramVariants, "reads");
    return this.coercePage<FusionConnectRead>(res, params.pageSize);
  }

  async fetchDailyStats(params: {
    from: string;
    to: string;
    page?: number;
    pageSize?: number;
    boardId?: string;
  }): Promise<FusionConnectDailyStatPage> {
    if (!this.loginService.isloggedIn) {
      return this.emptyPage<FusionConnectDailyStat>();
    }

    const paramVariants = this.buildParamsVariants(params, ["from", "to", "boardId"]);
    const res = await this.getWithFallback<any>(DAILY_STATS_URLS, paramVariants, "daily stats");
    return this.coercePage<FusionConnectDailyStat>(res, params.pageSize);
  }

  async rebuildDailyStats(params: { from: string; to: string }): Promise<any> {
    if (!this.loginService.isloggedIn) {
      throw new Error("User is not logged in");
    }

    const httpParams = new HttpParams()
      .set("from", params.from)
      .set("to", params.to);

    return await firstValueFrom(this.http.post<any>(DAILY_STATS_REBUILD_URL, {}, { params: httpParams }));
  }

  async flagDailyStat(params: { id: string; flagged: boolean; note?: string }): Promise<void> {
    if (!this.loginService.isloggedIn) {
      throw new Error("User is not logged in");
    }

    await firstValueFrom(
      this.http.patch<void>(`${DAILY_STATS_MUTATION_BASE_URL}/${params.id}/flag`, {
        flagged: !!params.flagged,
        note: params.note || '',
      })
    );
  }

  async deleteDailyStat(id: string): Promise<void> {
    if (!this.loginService.isloggedIn) {
      throw new Error("User is not logged in");
    }

    await firstValueFrom(this.http.delete<void>(`${DAILY_STATS_MUTATION_BASE_URL}/${id}`));
  }

  async createRegularBoardStatFromLiveReads(params: {
    startReadId: string;
    endReadId: string;
  }): Promise<any> {
    if (!this.loginService.isloggedIn) {
      throw new Error("User is not logged in");
    }

    return await firstValueFrom(
      this.http.post<any>(CREATE_REGULAR_BOARD_STAT_URL, {
        startReadId: params.startReadId,
        endReadId: params.endReadId,
      })
    );
  }

  async fetchGameMetersForBoardDay(params: {
    boardId: string;
    date: string;
    cashIn?: number;
    cashOut?: number;
    coinIn?: number;
    coinOut?: number;
    gamesPlayed?: number;
    gamesWon?: number;
  }): Promise<FusionConnectGameMeterRow[]> {
    if (!this.loginService.isloggedIn || !params?.boardId || !params?.date) {
      return [];
    }

    let httpParams = new HttpParams()
      .set("boardId", params.boardId)
      .set("date", params.date);

    if (params.cashIn !== undefined) httpParams = httpParams.set("cashIn", String(params.cashIn));
    if (params.cashOut !== undefined) httpParams = httpParams.set("cashOut", String(params.cashOut));
    if (params.coinIn !== undefined) httpParams = httpParams.set("coinIn", String(params.coinIn));
    if (params.coinOut !== undefined) httpParams = httpParams.set("coinOut", String(params.coinOut));
    if (params.gamesPlayed !== undefined) httpParams = httpParams.set("gamesPlayed", String(params.gamesPlayed));
    if (params.gamesWon !== undefined) httpParams = httpParams.set("gamesWon", String(params.gamesWon));

    return await firstValueFrom(
      this.http.get<FusionConnectGameMeterRow[]>(`${READS_URLS[0]}/game-meters`, { params: httpParams })
    );
  }

  // ---------- helpers ----------
  private buildParamsVariants(params: any, passthroughKeys: string[]): HttpParams[] {
    const page = Number.isInteger(params?.page) ? params.page : 0;
    const size = params?.pageSize ?? params?.size ?? 50;

    const variants: HttpParams[] = [
      new HttpParams().set("page", String(page)).set("pageSize", String(size)),
      new HttpParams().set("page", String(page)).set("size", String(size)),
      new HttpParams(),
    ];

    passthroughKeys.forEach((key) => {
      if (params?.[key] !== undefined && params?.[key] !== null && params?.[key] !== "") {
        for (let i = 0; i < variants.length; i++) {
          variants[i] = variants[i].set(key, String(params[key]));
        }
      }
    });

    const seen = new Set<string>();
    return variants.filter((item) => {
      const signature = item.toString();
      if (seen.has(signature)) {
        return false;
      }
      seen.add(signature);
      return true;
    });
  }

  private async getWithFallback<T>(urls: string[], paramVariants: HttpParams[], label: string): Promise<T> {
    let lastError: any;

    for (const url of urls) {
      for (const params of paramVariants) {
        try {
          return await firstValueFrom(this.http.get<T>(url, { params }));
        } catch (err: any) {
          lastError = err;
          console.warn(`[FusionConnect] ${label} request failed`, {
            url,
            params: params.toString(),
            status: err?.status,
            message: err?.message,
            error: err?.error,
          });
        }
      }
    }

    throw lastError;
  }

  private coercePage<T>(res: any, fallbackSize = 50): Page<T> {
    if (res?.content && Array.isArray(res.content)) {
      return res as Page<T>;
    }

    if (Array.isArray(res)) {
      const size = res.length || fallbackSize;
      return {
        content: res,
        totalElements: res.length,
        totalPages: 1,
        size,
        number: 0,
      };
    }

    return this.emptyPage<T>(fallbackSize);
  }

  private emptyPage<T>(size = 50): Page<T> {
    return { content: [], totalElements: 0, totalPages: 0, size, number: 0 };
  }
}
